/**
 * Ф4: вкладка «Каса» — платіжний календар, контроль буфера, лента банку.
 *
 * Календар не зберігається: він виводиться з затвердженого бюджету (виручка за
 * декадами, графік кредиту, довідник OPEX), тож тут показується розрахований
 * бекендом. Кожна позиція несе джерело суми (факт / номінація / план двигуна) і
 * позначку, якщо день оплати ПРИПУЩЕНО, а не заданий довідником.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useState } from 'react';
import {
  Wallet, ArrowLeftRight, FileClock, AlertTriangle, Check, X, Upload, Landmark,
  type LucideIcon,
} from 'lucide-react';
import { API_BASE_URL, authFetch } from '@/services/api';
import type { CashResp } from '../types';
import { mln, n0, n2 } from '../types';

const BASIS_LABEL: Record<string, string> = {
  get fact() { return i18n.t('ak_bud2_actual'); },
  get mms() { return i18n.t('ak_bud2_mms_nomination'); },
  get engine() { return i18n.t('ak_bud2_engine_plan'); },
  get plan() { return i18n.t('ak_bud2_plan'); },
};
const basisText = (b?: string | null) =>
  (b || '').split('+').map((x) => BASIS_LABEL[x] || x).filter(Boolean).join(' + ');

const dmy = (iso: string) => (iso ? iso.slice(8, 10) + '.' + iso.slice(5, 7) : '—');
const sgn = (v: number) => (v > 0 ? '+' : '');

function Panel({ Icon, title, hint, children }: {
  Icon: LucideIcon; title: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div className="tile p-4">
      <div className="flex items-start justify-between gap-3 pb-3 mb-3"
        style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
          <span className="text-[12.5px] font-semibold truncate" style={{ color: 'var(--text)' }}>{title}</span>
        </div>
        {hint && <span className="text-[11px] shrink-0 text-right" style={{ color: 'var(--text-dim)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

const TH: React.CSSProperties = {
  background: 'var(--surface-2)', color: 'var(--text-muted)', fontSize: 11,
  fontWeight: 600, padding: '7px 10px', whiteSpace: 'nowrap', textAlign: 'right',
};
const TD: React.CSSProperties = {
  fontSize: 12, padding: '6px 10px', whiteSpace: 'nowrap', textAlign: 'right',
  borderTop: '1px solid var(--line)', color: 'var(--text)',
  fontVariantNumeric: 'tabular-nums',
};

export default function CashSection({ data, assetId, month, onChanged }: {
  data: CashResp; assetId: string; month: string; onChanged: () => void;
}) {
  const [busy, setBusy] = useState('');
  const { t } = useTranslation();
  const [opening, setOpening] = useState('');

  const saveOpening = async () => {
    const v = Number(String(opening).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(v)) { window.alert(t('ak_bud2_enter_the_opening_balance_amount')); return; }
    setBusy('opening');
    try {
      const r = await authFetch(`${API_BASE_URL}/budget/cash/opening`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, onDate: `${month}-01`, amountUah: v }),
      });
      if (!r.ok) window.alert(t('ak_bud2_failed_to_save_http', { s: r.status }));
    } catch { window.alert(t('ak_bud2_failed_to_save_the_opening_balance')); }
    finally { setBusy(''); onChanged(); }
  };

  const decide = async (lineId: number, reject: boolean) => {
    setBusy(`m${lineId}`);
    try {
      const r = await authFetch(`${API_BASE_URL}/budget/match/${lineId}/confirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reject }),
      });
      if (!r.ok) window.alert(t('ak_bud2_failed_http', { s: r.status }));
    } catch { window.alert(t('ak_bud2_failed_to_update_the_matching')); }
    finally { setBusy(''); onChanged(); }
  };

  const b = data.buffer;

  return (
    <div className="grid grid-cols-1 gap-3">
      {/* Зведення руху коштів — один рядок плиткою, без вкладених боксів. */}
      <div className="tile p-4">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-3">
          {[
            [t('ak_bud2_opening_balance'), data.opening == null ? '—' : mln(data.opening), data.openingSource === 'none'
              ? t('ak_bud2_no_statement_and_no_manual_value') : t('ak_bud2_source', { s: data.openingSource })],
            [t('ak_bud2_receipts'), mln(data.inflow), t('ak_bud2_by_ten_day_periods_minus_margin')],
            [t('ak_bud2_payments'), mln(data.outflow), t('ak_bud2_loan_opex_imbalances')],
            [t('ak_bud2_net_for_the_month'), `${sgn(data.net)}${mln(data.net)}`, ''],
            [t('ak_bud2_end_of_month'), data.closing == null ? '—' : mln(data.closing),
              (b.bigTriggered ? t('ak_bud2_buffer_mn_large_loan_principal', { b: mln(b.needed, 0) }) : t('ak_bud2_buffer_mn', { b: mln(b.needed, 0) }))],
          ].map(([tl, v, note]) => (
            <div key={tl as string}>
              <div className="text-[11px] mb-0.5" style={{ color: 'var(--text-muted)' }}>{tl}</div>
              <div className="font-display text-xl leading-none"
                style={{ color: v === '—' ? 'var(--text-dim)' : 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                {v} <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{t('ak_bud2_mn_3')}</span>
              </div>
              {note && <div className="text-[10.5px] mt-1" style={{ color: 'var(--text-dim)' }}>{note}</div>}
            </div>
          ))}
        </div>

        {data.opening == null && (
          <div className="mt-4 pt-3 flex items-center gap-2 flex-wrap"
            style={{ borderTop: '1px solid var(--line)' }}>
            <Landmark size={14} strokeWidth={2} style={{ color: 'var(--warn)' }} />
            <span className="text-[11.5px]" style={{ color: 'var(--warn)' }}>
              {t('ak_bud2_without_an_opening_balance_the_cash_fo', { d: dmy(`${month}-01`) })}
            </span>
            <input value={opening} onChange={(e) => setOpening(e.target.value)}
              placeholder={t('ak_bud2_uah')} inputMode="decimal"
              style={{
                height: 30, width: 140, borderRadius: 8, fontSize: 12, padding: '0 9px',
                background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text)',
              }} />
            <button onClick={saveOpening} disabled={busy === 'opening'}
              className="inline-flex items-center gap-1.5 px-2.5 cursor-pointer"
              style={{
                height: 30, borderRadius: 8, fontSize: 11.5, background: 'var(--surface-2)',
                border: '1px solid var(--line)', color: 'var(--text)', opacity: busy ? 0.5 : 1,
              }}>
              <Check size={13} strokeWidth={2} /> {t('ak_bud2_save')}
            </button>
          </div>
        )}

        {b.breaches.length > 0 && (
          <div className="mt-3 pt-3 text-[11.5px] flex items-start gap-1.5"
            style={{ borderTop: '1px solid var(--line)', color: 'var(--warn)' }}>
            <AlertTriangle size={13} strokeWidth={2} className="shrink-0 mt-0.5" />
            <span>
              {t('ak_bud2_forecast_balance_falls_below_the_buffe', { b: mln(b.needed, 0) })}
              {' '}{b.breaches.map((x) => t('ak_bud2_mn_2', { d: dmy(x.dueDate), v: mln(x.balance) })).join(' · ')}.{' '}
              {t('ak_bud2_treasurer_decision_required_postpone_t')}
            </span>
          </div>
        )}
      </div>

      <Panel Icon={Wallet} title={t('ak_bud2_payment_calendar')}
        hint={t('ak_bud2_items', { n: data.items.length, m: data.month })}>
        <div className="overflow-x-auto -mx-4 px-4">
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ ...TH, textAlign: 'left' }}>{t('ak_bud2_date')}</th>
                <th style={{ ...TH, textAlign: 'left' }}>{t('ak_bud2_item')}</th>
                <th style={TH}>{t('ak_bud2_amount_uah')}</th>
                <th style={TH}>{t('ak_bud2_balance_after')}</th>
                <th style={{ ...TH, textAlign: 'left' }}>{t('ak_bud2_basis')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i, k) => (
                <tr key={`${i.dueDate}-${i.code}-${k}`}>
                  <td style={{ ...TD, textAlign: 'left' }}>{dmy(i.dueDate)}</td>
                  <td style={{ ...TD, textAlign: 'left', whiteSpace: 'normal' }}>
                    {i.name}
                    {i.assumedPayDay && (
                      <span className="text-[10px] ml-1.5" style={{ color: 'var(--warn)' }}>
                        {t('ak_bud2_payment_day_assumed')}
                      </span>
                    )}
                    {i.detail && (
                      <span className="block text-[10px]" style={{ color: 'var(--text-dim)' }}>{i.detail}</span>
                    )}
                  </td>
                  <td style={{ ...TD, color: i.amount > 0 ? 'var(--ok)' : 'var(--text)' }}>
                    {sgn(i.amount)}{n0(i.amount)}
                  </td>
                  <td style={{ ...TD, color: i.belowBuffer ? 'var(--warn)' : 'var(--text)' }}>
                    {i.balanceAfter == null ? '—' : n0(i.balanceAfter)}
                  </td>
                  <td style={{ ...TD, textAlign: 'left', color: 'var(--text-dim)', fontSize: 11 }}>
                    {basisText(i.basis)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[10.5px] mt-3" style={{ color: 'var(--text-dim)' }}>
          {t('ak_bud2_receipts_by_the_ten_day_rule_1_10_15th')}
          {data.assumptions.opexPayDayAssumed && (
            <> {t('ak_bud2_payment_days_of_the_fixed_items_are_no', { d: data.assumptions.opexDefaultPayDay })}</>
          )}
          {!data.assumptions.taxIncluded && <> {data.assumptions.taxNote}.</>}
        </div>
      </Panel>

      <Panel Icon={ArrowLeftRight} title={t('ak_bud2_bank_operations_and_matching')}
        hint={data.bank.count
          ? t('ak_bud2_rows_to_confirm_unmatched', { n: data.bank.count, c: data.bank.toConfirm, u: data.bank.unmatched })
          : t('ak_bud2_no_statements_uploaded_yet')}>
        {data.bank.count === 0 ? (
          <div className="text-[12.5px] py-5 text-center leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            {t('ak_bud2_no_statements_ingest_is_implemented_fo')}
            {' '}<code style={{ fontSize: 11 }}>date;amount;currency;counterparty;purpose;doc_no;account;balance_after</code>
            {' '}{t('ak_bud2_with_protection_against_re_upload_and')}
            <br />{t('ak_bud2_it_is_adapted_to_the_real_bank_format')}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 px-4">
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 860 }}>
              <thead>
                <tr>
                  <th style={{ ...TH, textAlign: 'left' }}>{t('ak_bud2_date')}</th>
                  <th style={TH}>{t('ak_bud2_amount_uah')}</th>
                  <th style={{ ...TH, textAlign: 'left' }}>{t('ak_bud2_counterparty')}</th>
                  <th style={{ ...TH, textAlign: 'left' }}>{t('ak_bud2_purpose')}</th>
                  <th style={{ ...TH, textAlign: 'left' }}>{t('ak_bud2_item')}</th>
                  <th style={TH}>{t('ak_bud2_conf')}</th>
                  <th style={{ ...TH, textAlign: 'left' }}>{t('ak_bud2_status')}</th>
                </tr>
              </thead>
              <tbody>
                {data.bank.lines.map((l) => {
                  const st = l.status || 'unmatched';
                  const color = st === 'auto' || st === 'confirmed' ? 'var(--ok)'
                    : st === 'rejected' ? 'var(--text-dim)' : 'var(--warn)';
                  return (
                    <tr key={l.id}>
                      <td style={{ ...TD, textAlign: 'left' }}>{dmy(l.op_date)}</td>
                      <td style={{ ...TD, color: l.amount > 0 ? 'var(--ok)' : 'var(--text)' }}>
                        {sgn(l.amount)}{n0(l.amount)}
                      </td>
                      <td style={{ ...TD, textAlign: 'left' }}>{l.counterparty || '—'}</td>
                      <td style={{ ...TD, textAlign: 'left', whiteSpace: 'normal', fontSize: 11 }}>
                        {l.purpose || '—'}
                        {l.match_note && (
                          <span className="block text-[10px]" style={{ color: 'var(--text-dim)' }}>{l.match_note}</span>
                        )}
                      </td>
                      <td style={{ ...TD, textAlign: 'left', fontSize: 11 }}>{l.budget_item || '—'}</td>
                      <td style={TD}>{l.confidence == null ? '—' : n2(l.confidence)}</td>
                      <td style={{ ...TD, textAlign: 'left' }}>
                        <span style={{ color, fontSize: 11 }}>
                          {st === 'auto' ? t('ak_bud2_automatic') : st === 'to_confirm' ? t('ak_bud2_to_confirm')
                            : st === 'confirmed' ? t('ak_bud2_confirmed') : st === 'rejected' ? t('ak_bud2_rejected') : t('ak_bud2_unmatched')}
                        </span>
                        {st === 'to_confirm' && (
                          <span className="inline-flex items-center gap-1 ml-2">
                            <button onClick={() => decide(l.id, false)} disabled={!!busy}
                              title={t('ak_bud2_confirm_the_match')} aria-label={t('ak_bud2_confirm')}
                              className="inline-flex items-center justify-center cursor-pointer"
                              style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--ok)' }}>
                              <Check size={12} strokeWidth={2.5} />
                            </button>
                            <button onClick={() => decide(l.id, true)} disabled={!!busy}
                              title={t('ak_bud2_reject_the_match')} aria-label={t('ak_bud2_reject')}
                              className="inline-flex items-center justify-center cursor-pointer"
                              style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text-muted)' }}>
                              <X size={12} strokeWidth={2.5} />
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-[10.5px] mt-3 flex items-start gap-1.5" style={{ color: 'var(--text-dim)' }}>
          <Upload size={12} strokeWidth={2} className="shrink-0 mt-0.5" />
          <span>
            {t('ak_bud2_spec_b5_1_thresholds_confidence_0_90_a')}
          </span>
        </div>
      </Panel>

      <Panel Icon={FileClock} title={t('ak_bud2_accounts_payable')}
        hint={t('ak_bud2_awaiting_access_to_accounting')}>
        <div className="text-[12.5px] py-5 text-center leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          {t('ak_bud2_accrued_per_acts_versus_paid_per_state')}
          <br />{t('ak_bud2_table')} <b>acc_entry</b> {t('ak_bud2_and_the_matching_chain_are_ready_the_e')}
        </div>
      </Panel>
    </div>
  );
}
